import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Modal, Button, Form, Row, Col, Alert, Spinner, Badge, Table
} from 'react-bootstrap';
import {
  FaPlus, FaTrash, FaCalendarAlt, FaMoneyBillAlt, FaSave, FaTimes
} from 'react-icons/fa';

const MOTIFS = [
  { value: 'licenciement',  label: 'Licenciement' },
  { value: 'démission',     label: 'Démission' },
  { value: 'fin_chantier',  label: 'Fin de chantier' },
  { value: 'autre',         label: 'Autre' }
];

const emptyForm = {
  date_stc: '',
  motif: '',
  salaire_du: '',
  indemnite_conge: '',
  indemnite_licenciement: '',
  indemnite_preavis: '',
  autres_indemnites: '',
  observation: ''
};

const fmt = (v) => parseFloat(v || 0).toFixed(2);

const StcModal = ({ show, onHide, ouvrier, canWrite }) => {
  const [stcs, setStcs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);

  const fetchStcs = async () => {
    if (!ouvrier?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/stc/ouvrier/${ouvrier.id}`);
      setStcs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('❌ Erreur chargement des STC.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchStcs();
      setShowForm(false);
      setFormData({ ...emptyForm });
      setError('');
      setSuccess('');
    }
  }, [show, ouvrier?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const computedTotal = useMemo(() => {
    return (
      parseFloat(formData.salaire_du || 0) +
      parseFloat(formData.indemnite_conge || 0) +
      parseFloat(formData.indemnite_licenciement || 0) +
      parseFloat(formData.indemnite_preavis || 0) +
      parseFloat(formData.autres_indemnites || 0)
    );
  }, [formData]);

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    if (!formData.date_stc) {
      setError('⚠️ La date STC est obligatoire.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`/stc/ouvrier/${ouvrier.id}`, formData);
      setSuccess('✅ STC enregistré.');
      setShowForm(false);
      setFormData({ ...emptyForm });
      await fetchStcs();
    } catch (err) {
      setError(err.response?.data?.message || '❌ Erreur enregistrement STC.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setError('');
    setSuccess('');
    try {
      await axios.delete(`/stc/${id}`);
      setSuccess('✅ STC supprimé.');
      await fetchStcs();
    } catch (err) {
      setError(err.response?.data?.message || '❌ Erreur suppression STC.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCalendarAlt className="me-2" />
          STC — {ouvrier?.name}{ouvrier?.cin ? ` (${ouvrier.cin})` : ''}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {success && <Alert variant="success" className="py-2">{success}</Alert>}

        {/* Liste des STC existants */}
        {loading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Chargement...</span>
          </div>
        ) : stcs.length === 0 ? (
          <p className="text-muted">Aucun STC enregistré pour cet ouvrier.</p>
        ) : (
          <Table striped bordered hover size="sm" className="mb-3">
            <thead>
              <tr>
                <th>Date</th>
                <th>Motif</th>
                <th>Salaire dû</th>
                <th>Congés</th>
                <th>Licenciement</th>
                <th>Préavis</th>
                <th>Autres</th>
                <th><strong>Total</strong></th>
                {canWrite && <th></th>}
              </tr>
            </thead>
            <tbody>
              {stcs.map((s) => (
                <tr key={s.id}>
                  <td>{s.date_stc ? String(s.date_stc).split('T')[0] : '—'}</td>
                  <td>
                    {s.motif
                      ? (MOTIFS.find(m => m.value === s.motif)?.label || s.motif)
                      : '—'}
                  </td>
                  <td>{fmt(s.salaire_du)} DH</td>
                  <td>{fmt(s.indemnite_conge)} DH</td>
                  <td>{fmt(s.indemnite_licenciement)} DH</td>
                  <td>{fmt(s.indemnite_preavis)} DH</td>
                  <td>{fmt(s.autres_indemnites)} DH</td>
                  <td>
                    <Badge bg="primary">{fmt(s.total)} DH</Badge>
                  </td>
                  {canWrite && (
                    <td className="text-center">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(s.id)}
                        title="Supprimer ce STC"
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {/* Formulaire ajout */}
        {canWrite && !showForm && (
          <Button variant="success" size="sm" onClick={() => setShowForm(true)}>
            <FaPlus className="me-1" /> Ajouter un STC
          </Button>
        )}

        {showForm && (
          <div className="border rounded p-3 mt-2 bg-light">
            <h6 className="mb-3">Nouveau STC</h6>
            <Row className="g-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Date STC *</Form.Label>
                  <Form.Control type="date" name="date_stc" value={formData.date_stc} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Motif</Form.Label>
                  <Form.Select name="motif" value={formData.motif} onChange={handleChange}>
                    <option value="">— Sélectionner —</option>
                    {MOTIFS.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Salaire dû (DH)</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" name="salaire_du" value={formData.salaire_du} onChange={handleChange} placeholder="0.00" />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Indemnité congés (DH)</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" name="indemnite_conge" value={formData.indemnite_conge} onChange={handleChange} placeholder="0.00" />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Indemnité licenciement (DH)</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" name="indemnite_licenciement" value={formData.indemnite_licenciement} onChange={handleChange} placeholder="0.00" />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Préavis (DH)</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" name="indemnite_preavis" value={formData.indemnite_preavis} onChange={handleChange} placeholder="0.00" />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Autres indemnités (DH)</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" name="autres_indemnites" value={formData.autres_indemnites} onChange={handleChange} placeholder="0.00" />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Observation</Form.Label>
                  <Form.Control as="textarea" rows={2} name="observation" value={formData.observation} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex align-items-center justify-content-between mt-3">
              <div>
                <strong>Total : </strong>
                <Badge bg="primary" className="fs-6 ms-1">
                  <FaMoneyBillAlt className="me-1" />
                  {computedTotal.toFixed(2)} DH
                </Badge>
              </div>
              <div className="d-flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => setShowForm(false)} disabled={submitting}>
                  <FaTimes className="me-1" /> Annuler
                </Button>
                <Button variant="primary" size="sm" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? <Spinner animation="border" size="sm" className="me-1" /> : <FaSave className="me-1" />}
                  Enregistrer
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StcModal;
